import React, { useEffect, useMemo, useState } from 'react';
import {
  displayAnalyticsValue,
  displayUtmValue,
  formatDuration,
  formatTimestamp,
  shortId,
} from '../utils/analyticsDisplay';

const VISITORS_PAGE_SIZE = 12;

const ACCENT_STYLES = [
  {
    chip: 'bg-[#efe4d3] text-[#855f28]',
    border: 'border-[#e6d5bb]',
    panel: 'bg-[#fbf7f1]',
    dot: 'bg-[#b98333]',
  },
  {
    chip: 'bg-[#dff0ec] text-[#27645b]',
    border: 'border-[#c6e4dc]',
    panel: 'bg-[#f3faf8]',
    dot: 'bg-[#2f8d7c]',
  },
  {
    chip: 'bg-[#e3e8f7] text-[#415796]',
    border: 'border-[#d2dbf2]',
    panel: 'bg-[#f5f7fc]',
    dot: 'bg-[#5c75bb]',
  },
];

const VisitorGroupsPanel = ({ visitors = [], hasFilters = false }) => {
  const [visibleCount, setVisibleCount] = useState(VISITORS_PAGE_SIZE);
  const [expandedVisitors, setExpandedVisitors] = useState({});

  useEffect(() => {
    setVisibleCount(VISITORS_PAGE_SIZE);
    setExpandedVisitors({});
  }, [visitors]);

  const visibleVisitors = useMemo(
    () => visitors.slice(0, visibleCount),
    [visitors, visibleCount]
  );

  const hasMoreVisitors = visitors.length > visibleCount;

  const toggleVisitor = (userId) => {
    setExpandedVisitors((current) => ({
      ...current,
      [userId]: !current[userId],
    }));
  };

  if (visitors.length === 0) {
    return (
      <section className="rounded-[30px] border border-white/80 bg-white/75 p-8 shadow-[0_32px_80px_-48px_rgba(42,30,12,0.38)] backdrop-blur">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8a6b42]">Visitors</p>
            <h3 className="mt-2 text-2xl font-semibold text-[#1f1a14]">Dossiers</h3>
          </div>
          <span className="rounded-full bg-[#f0e6d8] px-4 py-2 text-sm font-medium text-[#7f6140]">
            0 visitors
          </span>
        </div>

        <div className="mt-10 rounded-[24px] border border-dashed border-[#d9c8b1] bg-[#fcfaf7] px-6 py-12 text-center">
          <p className="text-lg font-medium text-[#3c342b]">
            {hasFilters ? 'No matching visitors' : 'No visitors yet'}
          </p>
          <p className="mt-2 text-sm text-[#7f7469]">
            {hasFilters ? 'Clear filters to restore the full dossier list.' : 'New sessions will appear here.'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-[32px] border border-white/80 bg-white/78 p-6 shadow-[0_32px_90px_-50px_rgba(42,30,12,0.42)] backdrop-blur sm:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8a6b42]">Visitors</p>
          <h3 className="mt-2 text-2xl font-semibold text-[#1f1a14] sm:text-3xl">Dossiers</h3>
        </div>
        <div className="flex flex-wrap gap-3">
          <MetricPill label="Visitors" value={visitors.length} />
          <MetricPill
            label="Returning"
            value={visitors.filter((visitor) => Number(visitor.sessionCount) > 1).length}
          />
        </div>
      </div>

      <div className="mt-8 grid gap-5 xl:grid-cols-2">
        {visibleVisitors.map((visitor, index) => (
          <VisitorCard
            key={visitor.userId}
            visitor={visitor}
            accent={ACCENT_STYLES[index % ACCENT_STYLES.length]}
            expanded={Boolean(expandedVisitors[visitor.userId])}
            onToggle={() => toggleVisitor(visitor.userId)}
          />
        ))}
      </div>

      {hasMoreVisitors && (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount((current) => current + VISITORS_PAGE_SIZE)}
            className="rounded-full border border-[#d7c7b2] bg-[#f7efe3] px-5 py-3 text-sm font-semibold text-[#6a5132] transition hover:-translate-y-0.5 hover:bg-[#f2e4cf]"
          >
            Load more
          </button>
        </div>
      )}
    </section>
  );
};

const MetricPill = ({ label, value }) => (
  <div className="rounded-full border border-[#eadfce] bg-[#f8f2e8] px-4 py-2 text-sm text-[#6d5a43]">
    <span className="font-semibold text-[#2a231b]">{Number(value || 0).toLocaleString()}</span> {label}
  </div>
);

const VisitorCard = ({ visitor, accent, expanded, onToggle }) => {
  const recentSessions = Array.isArray(visitor.recentSessions) ? visitor.recentSessions : [];
  const latestSession = recentSessions[0];
  const firstAttribution = normalizeAttribution(visitor.firstAttribution || visitor.attribution);
  const latestAttribution = normalizeAttribution(visitor.latestAttribution || visitor.attribution || firstAttribution);
  const landingPath = displayAnalyticsValue(firstAttribution.landingPath || latestAttribution.landingPath, '/');
  const latestAction = resolveLatestAction(visitor, latestSession);
  const actionCounts = resolveActionCounts(visitor);
  const locationLabel = [visitor.location?.city, visitor.location?.region, visitor.location?.country]
    .map((value) => displayAnalyticsValue(value, ''))
    .filter(Boolean)
    .join(', ');
  const primaryDevice = displayAnalyticsValue(visitor.profile?.deviceType, 'Unknown device');
  const primaryBrowser = displayAnalyticsValue(visitor.profile?.browserName, 'Unknown browser');
  const recentActions = sortRecentActions(visitor.recentActions);

  return (
    <article className={`rounded-[28px] border p-5 transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-36px_rgba(51,40,24,0.45)] sm:p-6 ${accent.border} ${accent.panel}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-[#776555]">
              {shortId(visitor.userId)}
            </span>
            {Number(visitor.sessionCount) > 1 && (
              <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${accent.chip}`}>
                Returning
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-xs font-medium text-[#655a4d]">
            <Tag value={locationLabel || 'Unknown location'} />
            <Tag value={`${primaryDevice} / ${primaryBrowser}`} />
          </div>
        </div>

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="inline-flex items-center justify-center rounded-full border border-white/90 bg-white/90 px-4 py-2 text-sm font-semibold text-[#45392c] transition hover:bg-white"
        >
          {expanded ? 'Hide details' : 'Show details'}
        </button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatBlock label="Sessions" value={Number(visitor.sessionCount || 0).toLocaleString()} />
        <StatBlock label="Page views" value={Number(visitor.pageViews || 0).toLocaleString()} />
        <StatBlock label="Avg duration" value={formatDuration(visitor.avgSessionDurationMs)} />
        <StatBlock label="Last seen" value={formatTimestamp(visitor.lastSeenAt)} valueClassName="text-sm leading-5" />
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-3">
        <SourceBlock
          label="First touch"
          primary={formatAttributionSource(firstAttribution)}
          secondary={displayUtmValue(firstAttribution.utmMedium, '')}
        />
        <SourceBlock
          label="Latest touch"
          primary={formatAttributionSource(latestAttribution)}
          secondary={displayUtmValue(latestAttribution.utmMedium, '')}
        />
        <SourceBlock
          label="Landing"
          primary={landingPath}
          secondary={displayAnalyticsValue(latestSession?.lastPath || latestSession?.entryPath, '/')}
        />
      </div>

      <div className="mt-4 rounded-[22px] border border-white/80 bg-white/75 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8b7a68]">Latest</p>
            <p className="mt-2 text-sm font-medium text-[#2b251e]">
              {displayAnalyticsValue(latestSession?.lastPath || latestSession?.entryPath, '/')}
            </p>
          </div>
          <div className="text-right text-sm text-[#66594c]">
            <p>{displayAnalyticsValue(visitor.network?.isp, 'Unknown ISP')}</p>
            <p className="mt-1 text-xs text-[#8b7f72]">
              {displayAnalyticsValue(visitor.network?.asn, 'No ASN')}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Tag value={`Last action: ${latestAction.label}`} />
          <Tag value={formatTimestamp(latestAction.ts)} />
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <IntentBlock label="Resume" value={actionCounts.resumeOpen} />
        <IntentBlock label="Social" value={actionCounts.socialClick} />
        <IntentBlock label="Contact" value={actionCounts.contactClick} />
        <IntentBlock label="PDF" value={actionCounts.paperPdfOpen} />
        <IntentBlock label="Code" value={actionCounts.paperCodeClick} />
      </div>

      {expanded && (
        <div className="mt-6 border-t border-white/90 pt-6">
          <div className="grid gap-6 xl:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8b7a68]">Sessions</p>
              <ol className="mt-4 space-y-4">
                {recentSessions.map((session) => (
                  <SessionTimelineEntry
                    key={session.sessionId}
                    accent={accent}
                    session={session}
                  />
                ))}
              </ol>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8b7a68]">Recent actions</p>
              {recentActions.length === 0 ? (
                <div className="mt-4 rounded-[22px] border border-dashed border-[#dac8b4] bg-[#fcfaf7] px-4 py-6">
                  <p className="text-sm font-medium text-[#3c342b]">No recent actions</p>
                  <p className="mt-1 text-xs text-[#7f7469]">Custom intent events will appear here.</p>
                </div>
              ) : (
                <ol className="mt-4 space-y-3">
                  {recentActions.map((action) => (
                    <RecentActionEntry key={`${action.eventType}-${action.ts}-${action.path}`} action={action} />
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

const StatBlock = ({ label, value, valueClassName = '' }) => (
  <div className="rounded-[20px] border border-white/80 bg-white/80 p-4">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8b7a68]">{label}</p>
    <p className={`mt-2 text-xl font-semibold text-[#241f19] ${valueClassName}`.trim()}>{value}</p>
  </div>
);

const Tag = ({ value }) => (
  <span className="rounded-full border border-white/90 bg-white/85 px-3 py-1.5">
    {value}
  </span>
);

const SourceBlock = ({ label, primary, secondary }) => (
  <div className="rounded-[20px] border border-white/80 bg-white/80 p-4">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8b7a68]">{label}</p>
    <p className="mt-2 text-sm font-semibold text-[#241f19]">{displayAnalyticsValue(primary, 'direct')}</p>
    <p className="mt-1 text-xs text-[#7c6f61]">{displayAnalyticsValue(secondary, '') || ' '}</p>
  </div>
);

const IntentBlock = ({ label, value }) => (
  <div className="rounded-[20px] border border-white/80 bg-white/80 p-4">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8b7a68]">{label}</p>
    <p className="mt-2 text-xl font-semibold text-[#241f19]">{Number(value || 0).toLocaleString()}</p>
  </div>
);

const SessionTimelineEntry = ({ accent, session }) => {
  const sessionLocation = [session.location?.city, session.location?.country]
    .map((value) => displayAnalyticsValue(value, ''))
    .filter(Boolean)
    .join(', ');
  const deviceLabel = displayAnalyticsValue(session.profile?.deviceType, 'Unknown device');

  return (
    <li className="relative pl-8">
      <span className={`absolute left-0 top-3 h-3.5 w-3.5 rounded-full ring-4 ring-white ${accent.dot}`}></span>
      <div className="rounded-[22px] border border-white/90 bg-white/82 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-[#241f19]">{formatTimestamp(session.firstSeenAt)}</p>
            <p className="mt-1 text-xs text-[#7c6f61]">
              {sessionLocation || 'Unknown location'} • {deviceLabel}
            </p>
          </div>
          <div className="text-sm text-[#5f5448] sm:text-right">
            <p>{Number(session.pageViews || 0).toLocaleString()} pages</p>
            <p className="mt-1">{formatDuration(session.durationMs)}</p>
          </div>
        </div>

        <div className="mt-4 grid gap-2 text-sm text-[#3f382f]">
          <SessionPathRow label="Entry" value={displayAnalyticsValue(session.entryPath, '/')} />
          <SessionPathRow label="Last path" value={displayAnalyticsValue(session.lastPath || session.entryPath, '/')} />
        </div>
      </div>
    </li>
  );
};

const RecentActionEntry = ({ action }) => {
  const title = humanizeEventType(action.eventType);
  const source = formatActionSource(action);
  const path = displayAnalyticsValue(action.path, '/');

  return (
    <li className="rounded-[22px] border border-white/90 bg-white/82 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-[#fbf6ee] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4b3c2e]">
              {title}
            </span>
            {action.details?.surface && (
              <span className="rounded-full bg-[#f4f0e8] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6d5a43]">
                {displayAnalyticsValue(action.details.surface, '')}
              </span>
            )}
          </div>
          <p className="mt-2 truncate text-sm font-medium text-[#2b241d]" title={path}>
            {path}
          </p>
          <p className="mt-1 truncate text-xs text-[#7d7266]" title={source}>
            {source}
          </p>
        </div>
        <span className="shrink-0 text-xs text-[#84796d]">
          {formatTimestamp(action.ts)}
        </span>
      </div>
    </li>
  );
};

const SessionPathRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 rounded-2xl bg-[#faf6f0] px-3 py-2">
    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8a7a67]">{label}</span>
    <span className="max-w-[70%] truncate text-right font-medium text-[#2e2720]" title={value}>
      {value}
    </span>
  </div>
);

const normalizeAttribution = (attribution) => {
  const source = attribution && typeof attribution === 'object' ? attribution : {};
  return {
    landingPath: source.landingPath || '',
    referrerHost: source.referrerHost || '',
    utmSource: source.utmSource || '',
    utmMedium: source.utmMedium || '',
  };
};

const formatAttributionSource = (attribution) => {
  const referrerHost = displayAnalyticsValue(attribution?.referrerHost, 'direct');
  const utmSource = displayUtmValue(attribution?.utmSource, '');
  const parts = [referrerHost];
  if (utmSource) parts.push(utmSource);
  return parts.filter(Boolean).join(' · ') || 'direct';
};

const resolveActionCounts = (visitor) => {
  const counts = visitor?.actionCounts && typeof visitor.actionCounts === 'object'
    ? visitor.actionCounts
    : {};
  const recentActions = normalizeRecentActions(visitor?.recentActions);
  const derivedCounts = recentActions.reduce((acc, action) => {
    const key = actionTypeToCountKey(action.eventType);
    if (key) {
      acc[key] += 1;
    }
    return acc;
  }, {
    resumeOpen: 0,
    socialClick: 0,
    contactClick: 0,
    paperPdfOpen: 0,
    paperCodeClick: 0,
  });

  return {
    resumeOpen: toCount(counts.resumeOpen ?? derivedCounts.resumeOpen),
    socialClick: toCount(counts.socialClick ?? derivedCounts.socialClick),
    contactClick: toCount(counts.contactClick ?? derivedCounts.contactClick),
    paperPdfOpen: toCount(counts.paperPdfOpen ?? derivedCounts.paperPdfOpen),
    paperCodeClick: toCount(counts.paperCodeClick ?? derivedCounts.paperCodeClick),
  };
};

const resolveLatestAction = (visitor, latestSession) => {
  const recentActions = sortRecentActions(visitor?.recentActions);
  const lastAction = recentActions[0];

  if (lastAction) {
    return {
      label: humanizeEventType(lastAction.eventType),
      ts: lastAction.ts,
    };
  }

  if (visitor?.lastActionType) {
    return {
      label: humanizeEventType(visitor.lastActionType),
      ts: visitor.lastActionAt || latestSession?.lastSeenAt || visitor?.lastSeenAt || 0,
    };
  }

  return {
    label: 'No action',
    ts: latestSession?.lastSeenAt || visitor?.lastSeenAt || 0,
  };
};

const sortRecentActions = (recentActions) =>
  normalizeRecentActions(recentActions).slice(0, 5);

const normalizeRecentActions = (recentActions) =>
  [...(Array.isArray(recentActions) ? recentActions : [])]
    .map((action) => ({
      ...action,
      ts: Number(action?.ts || 0),
    }))
    .filter((action) => Boolean(action.eventType))
    .sort((a, b) => b.ts - a.ts);

const actionTypeToCountKey = (eventType) => {
  switch (eventType) {
    case 'resume_open':
      return 'resumeOpen';
    case 'social_click':
      return 'socialClick';
    case 'contact_click':
      return 'contactClick';
    case 'paper_pdf_open':
      return 'paperPdfOpen';
    case 'paper_code_click':
      return 'paperCodeClick';
    default:
      return null;
  }
};

const humanizeEventType = (eventType) => {
  switch (eventType) {
    case 'resume_open':
      return 'Resume open';
    case 'social_click':
      return 'Social click';
    case 'contact_click':
      return 'Contact click';
    case 'paper_pdf_open':
      return 'PDF open';
    case 'paper_code_click':
      return 'Code click';
    case 'external_link_click':
      return 'Outbound link';
    default:
      return displayAnalyticsValue(eventType, 'Event');
  }
};

const formatActionSource = (action) => {
  const attribution = action?.attribution && typeof action.attribution === 'object' ? action.attribution : {};
  const referrerHost = displayAnalyticsValue(attribution.referrerHost, 'direct');
  const utmSource = displayUtmValue(attribution.utmSource, '');
  const utmMedium = displayUtmValue(attribution.utmMedium, '');
  const parts = [referrerHost];
  if (utmSource) parts.push(utmSource);
  if (utmMedium) parts.push(utmMedium);
  return parts.filter(Boolean).join(' · ') || 'direct';
};

const toCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default VisitorGroupsPanel;
